import { Fragment, useEffect, useRef, useState } from "react";
import {
    Card,
    Col,
    Container,
    Input,
    Row,
    Button
} from "reactstrap";
import { API } from "../../api/API";
import { getData } from "../../CommonElements/Functions/PostGetFun";
import { ensureDexieReady, getFilteredPositionIds } from "../../db/DexieServises";
import CardHeaderSpan from "../../Utils/CommonComponents/CardHeaderSpan";
import TreeModel from "../Dashboards/Attendance/AttendanceTable/TreeModel";
import CommonTableNew from "../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";
import DatePicker from 'react-datepicker';
import { Calendar, Download, Search } from 'react-feather';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux-toolkit/store';
import { dynamicImage } from "../../Utils";
import { Image } from "../../AbstractElements";
import FeatherIconCom from "../../Utils/CommonComponents/CommonIcons/FeatherIconCom";
import 'react-datepicker/dist/react-datepicker.css';
import MonthDropdown from "../CommonCompo/MonthDropdown";
import { useParams } from "react-router-dom";

// Define the configuration for each report
const reportConfigs: Record<string, any> = {
    'attendance-register-details': {
        title: "Attendance Register Details Report",
        api: API.PendingAttendanceReport,
        usePositionFilter: false,
        useDateFilter: false,
        params: []
    },
    'employee-activity-performance': {
        title: "Employee Activity Performance Report",
        api: API.getDarReport,
        usePositionFilter: false,
        useDateFilter: false,
        params: []
    },
    'product-wise-support': {
        title: "Product wise support Report",
        api: API.getSupportCaptureSummary,
        usePositionFilter: true,
        useMonthFilter: true,
        params: ['moye', 'position_id']
    },
    'primary-vs-secondary': {
        title: "Primary Vs Secondary Report",
        api: "api/getPrimarySecondaryStockistReport",
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye', 'org_unit_id']
    },
    'visit-activity': {
        title: "Visit Activity Report",
        api: "api/reports/visitActivityReport",
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye']
    },
    'sample-report': {
        title: "Sample Report",
        api: API.getSgpiOutReport,
        usePositionFilter: false,
        useDateFilter: false,
        params: []
    },
    'call-average': {
        title: "Call Average Report",
        api: "api/getAverageReport",
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye', 'org_unit_id']
    },
    'target-achievement': {
        title: "Target Achievement Report",
        api: "api/getTargetAchievementReport",
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['org_unit_id', 'moye']
    },
    'employee-claim': {
        title: "Employee Claim Report",
        api: "api/getEmployeeClaimReport",
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye', 'org_unit_id']
    },
    'rcpa-report': {
        title: "RCPA Report",
        api: API.getRcpaSkuSummary,
        usePositionFilter: true,
        useMonthFilter: true,
        params: ['moye', 'position_id']
    }
};

const CommonReport = () => {
    const { reportType } = useParams<{ reportType: string }>();
    const config = reportConfigs[reportType || ''];

    const [searchText, setSearchText] = useState('');
    const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
    const [reportData, setReportData] = useState<any[]>([]);
    const [displayedData, setDisplayedData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [selectedMoye, setSelectedMoye] = useState<string>(''); // MM-YYYY
    
    const serverUrl = localStorage.getItem('serverUrl');
    const APP_TOKEN = localStorage.getItem('appToken');
    const unitId = useSelector((state: RootState) => state.orgUnit.id);
    const datePickerRef = useRef<any>(null);

    const handleContainerClick = () => {
        if (datePickerRef.current) {
            datePickerRef.current.setFocus();
        }
    };

    const fetchData = async () => {
        if (!config || !serverUrl || !APP_TOKEN) return;
        setLoading(true);

        try {
            await ensureDexieReady();
            const dexieIds = await getFilteredPositionIds('Position', unitId);
            const positionIds = selectedIds.length > 0 ? selectedIds : (dexieIds || []);

            const payload: any = {};
            
            if (config.params.includes('position_id')) {
                payload.position_id = positionIds;
            }
            if (config.params.includes('date')) {
                payload.date = startDate?.toISOString().split('T')[0] || '';
            }
            if (config.params.includes('moye')) {
                payload.moye = selectedMoye;
            }
            if (config.params.includes('org_unit_id')) {
                payload.org_unit_id = unitId;
            }

            const url = `${serverUrl}${config.api}`;
            const { status, data } = await getData(url, true, payload);

            if (status === 200 && (Array.isArray(data?.data) || Array.isArray(data))) {
                setReportData(data?.data || data);
            } else {
                setReportData([]);
            }
        } catch (error) {
            console.error("Error fetching report data:", error);
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (config) {
            // Only fetch if moye is set (if required)
            if (config.useMonthFilter && !selectedMoye) return;
            fetchData();
        }
    }, [reportType, startDate, selectedMoye, selectedIds, unitId]);

    useEffect(() => {
        const lower = searchText.toLowerCase();
        const filtered = reportData.filter((item) => {
            return Object.values(item).some(val => 
                String(val).toLowerCase().includes(lower)
            );
        });
        setDisplayedData(filtered);
    }, [searchText, reportData]);

    const keys = reportData.length > 0 ? Object.keys(reportData[0]) : [];
    const captionTableHeader = keys.map((key, index) => ({
        id: index.toString(),
        head: key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim().toUpperCase(),
        key: key
    }));

    const handleExportToExcel = () => {
        if (displayedData.length === 0) return;
        
        const worksheet = XLSX.utils.json_to_sheet(displayedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, `${config?.title || 'Report'}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    if (!config) {
        return <div className="p-4">Report not found.</div>;
    }

    return (
        <div className='page-body p-2'>
            <Card>
                <div className='d-flex justify-content-between align-items-center pt-2 pb-2 px-3'>
                    <CardHeaderSpan 
                        headingClassName='pb-0' 
                        back={true} 
                        heading={config.title} 
                        span="Report" 
                    />
                    
                    {config.useMonthFilter ? (
                         <div className="p-1">
                            <MonthDropdown onChange={(moye) => setSelectedMoye(moye)} />
                         </div>
                    ) : config.useDateFilter && (
                        <div 
                            onClick={handleContainerClick}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '200px',
                                border: 'none',
                                borderRadius: '5px',
                                padding: '6px 12px',
                                cursor: 'pointer'
                            }}
                            className="bg-primary"
                        >
                            <Calendar size={18} color="#fff" />
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                maxDate={new Date()}
                                dateFormat="MMMM dd, yyyy"
                                className="form-control border-0 p-0 text-light bg-transparent"
                                wrapperClassName="w-100"
                                ref={datePickerRef}
                            />
                        </div>
                    )}
                </div>

                <Container fluid className="mt-3">
                    <Row className="mb-3 align-items-center justify-content-between">
                        <Col md={8} className="d-flex align-items-center gap-3 flex-wrap">
                            <div className="position-relative" style={{ maxWidth: "300px", width: "100%" }}>
                                <Input
                                    type="text"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder="Search in report..."
                                    className="form-control ps-4"
                                />
                                <Search size={14} className="position-absolute translate-middle-y top-50 start-0 ms-2 text-muted" />
                            </div>
                            {config.usePositionFilter && (
                                <TreeModel onSelectionChange={setSelectedIds} />
                            )}
                        </Col>
                        
                        <Col md={4} className="d-flex justify-content-end gap-3">
                            <Button 
                                color="primary" 
                                size="sm" 
                                className="d-flex align-items-center gap-2"
                                onClick={handleExportToExcel}
                                disabled={displayedData.length === 0}
                            >
                                <Download size={14} />
                                <span>Export</span>
                            </Button>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={12}>
                            <div className="table-responsive" style={{ minHeight: '400px' }}>
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Image
                                            className="b-r-10 img-80"
                                            src={dynamicImage('dashboard/loader.gif')}
                                            alt="Loading..."
                                        />
                                    </div>
                                ) : (
                                    <CommonTableNew
                                        caption=""
                                        headData={captionTableHeader}
                                        data={displayedData}
                                        renderRow={(row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {keys.map((key, colIndex) => (
                                                    <td key={colIndex}>
                                                        {typeof row[key] === 'boolean' 
                                                            ? (row[key] ? "Yes" : "No") 
                                                            : (row[key] ?? "-")}
                                                    </td>
                                                ))}
                                            </tr>
                                        )}
                                    />
                                )}
                                {!loading && displayedData.length === 0 && (
                                    <div className="text-center py-4 text-muted">
                                        No data found for the selected criteria.
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </Card>
        </div>
    );
};

export default CommonReport;
